import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import '../../styles/locations.css';
import { api } from '../../lib/api.js';
import { SourcingTag } from '../../components/StatusTag.js';
import { InventorySearch } from '../../components/InventorySearch.js';
import { QueryState } from '../../components/QueryState.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { MatchingTab } from './MatchingTab.js';
import { ConversationEntry } from '../../components/conversations/ConversationEntry.js';
import { POINTED_AT_STYLE, useScrollIntoViewWhen } from '../../lib/useScrollIntoViewWhen.js';

interface ProducerSafePoi {
  sourceId: number;
  displayLabel: string;
  approximateLatitude: number | null;
  approximateLongitude: number | null;
  discoveredMonth: string | null;
  readiness: string;
}

/**
 * THE TAB'S FOUR VIEWS, one at a time (2026-10-30). The tab used to be every
 * activity at once — the search form, the matches, the whole catalogue, the
 * requirements and the points of interest, one under the other, 23 000 to
 * 40 000 pixels tall. They are different jobs, so they are different views;
 * the view is in the address (`?view=`) like every other state worth sending.
 */
type View = 'matches' | 'catalogue' | 'requirements' | 'pois';

const VIEWS: readonly { id: View; label: string }[] = [
  { id: 'matches', label: 'Matches' },
  { id: 'catalogue', label: 'Search the catalogue' },
  { id: 'requirements', label: 'Requirements' },
  { id: 'pois', label: 'Points of interest' },
];

function parseView(value: string | null): View | null {
  return VIEWS.find((v) => v.id === value)?.id ?? null;
}

export function LocationsTab({
  projectId,
  projectTitle,
  onCompare,
}: {
  projectId: string;
  projectTitle: string;
  onCompare: (requirementId: string) => void;
}) {
  const script = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
  });
  const scriptVersionLabel = script.data ? `V${String(script.data.versionNumber)}` : '';

  const sourced = useQuery({
    queryKey: ['matchable', projectId],
    queryFn: () => api.matchableRequirements(projectId),
  });

  const pois = useQuery({
    queryKey: ['pois', projectId],
    queryFn: () => api.listPois(projectId) as Promise<ProducerSafePoi[]>,
  });

  const [params, setParams] = useSearchParams();
  const rawPlace = params.get('place');
  const focusPlaceId = rawPlace !== null && /^\d{1,10}$/.test(rawPlace) ? Number(rawPlace) : null;

  // A link that points at a scout's find opens the points of interest; any
  // other place is looked for among the matches.
  const pointsAtPoi = focusPlaceId !== null && (pois.data ?? []).some((p) => p.sourceId === focusPlaceId);
  const view: View = parseView(params.get('view')) ?? (pointsAtPoi ? 'pois' : 'matches');

  const setView = (next: View) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'matches') nextParams.delete('view');
    else nextParams.set('view', next);
    // The pointed-at place belongs to the view the link opened.
    nextParams.delete('place');
    setParams(nextParams, { replace: true });
  };

  const counts: Partial<Record<View, number>> = {
    ...(sourced.data === undefined ? {} : { requirements: sourced.data.length }),
    ...(pois.data === undefined ? {} : { pois: pois.data.length }),
  };

  return (
    <div data-testid="locations-tab" className="loc-page">
      <div className="loc-views" role="tablist" aria-label="Locations">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            role="tab"
            className="loc-view"
            aria-selected={view === v.id}
            data-testid={`locations-view-${v.id}`}
            onClick={() => {
              setView(v.id);
            }}
          >
            {v.label}
            {counts[v.id] === undefined ? null : <span className="loc-view-count">{String(counts[v.id])}</span>}
          </button>
        ))}
      </div>

      {/* Script -> locations: the constraints the screenplay cannot supply, and
          then every matched location once, however many scenes it serves. The
          title and version reach the exported location pack; without them it
          was headed "This production" with an empty version label. */}
      {view === 'matches' ? (
        <MatchingTab
          projectId={projectId}
          projectTitle={projectTitle}
          scriptVersionLabel={scriptVersionLabel}
          focusPlaceId={pointsAtPoi ? null : focusPlaceId}
        />
      ) : null}

      {view === 'catalogue' ? <InventorySearch projectId={projectId} /> : null}

      {view === 'requirements' ? (
        <section data-testid="locations-requirements">
          <div className="loc-section-head">
            <h3>Requirements being searched</h3>
            <p className="loc-section-note">
              Compare puts a requirement’s candidates side by side, axis by axis.
            </p>
          </div>

          <QueryState query={sourced} subject="matched locations" testId="matched" />

          {sourced.data && sourced.data.length === 0 ? (
            <EmptyState testId="matched-empty" title="You are not searching for anything yet.">
              Approve a location requirement in the breakdown, then press Start searching in Explore.
            </EmptyState>
          ) : null}

          {sourced.data && sourced.data.length > 0 ? (
            <ul className="loc-list">
              {sourced.data.map((r) => (
                <li key={r.id} data-testid={`matched-${r.id}`}>
                  <span className="loc-list-title">{r.title}</span>
                  <SourcingTag status={r.sourcingStatus} />
                  <span className="loc-list-end">
                    <ConversationEntry projectId={projectId} anchorType="REQUIREMENT" anchorId={r.id} label={r.title} />
                    <button
                      type="button"
                      className="ctl"
                      data-testid={`compare-${r.id}`}
                      onClick={() => {
                        onCompare(r.id);
                      }}
                    >
                      Compare
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      {view === 'pois' ? (
        <section data-testid="locations-pois">
          <div className="loc-section-head">
            <h3>Points of interest</h3>
            <p className="loc-section-note">
              Places a scout found that are not listed yet. You can shortlist and compare them, but
              one cannot be confirmed as a primary location until it is verified and promoted —
              nobody has agreed terms for it.
            </p>
          </div>

          {pois.data && pois.data.length === 0 ? (
            <EmptyState testId="pois-empty" title="None on this project yet." />
          ) : null}

          {pois.data && pois.data.length > 0 ? (
            <ul className="loc-list">
              {pois.data.map((poi) => (
                <PoiRow key={poi.sourceId} projectId={projectId} poi={poi} focused={focusPlaceId === poi.sourceId} />
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function PoiRow({ projectId, poi, focused }: { projectId: string; poi: ProducerSafePoi; focused: boolean }) {
  const ref = useScrollIntoViewWhen<HTMLLIElement>(focused);
  return (
    <li
      ref={ref}
      style={focused ? POINTED_AT_STYLE : undefined}
      data-testid={`poi-${String(poi.sourceId)}`}
      data-focused={focused ? '1' : undefined}
    >
      {/* A DERIVED label — never the venue's real name. The projection
          is the whole privacy boundary, and the UI must not undo it by
          showing something it was careful not to send. */}
      <span className="loc-list-title">{poi.displayLabel}</span>
      <span className="chip">{poi.readiness.toLowerCase()}</span>
      {poi.discoveredMonth ? (
        <span className="mm" style={{ fontSize: 11.5 }}>
          found {poi.discoveredMonth}
        </span>
      ) : null}
      {/* Approximate by design, and labelled so nobody navigates to it. */}
      <span className="mm" style={{ fontSize: 11.5 }}>
        {'Approximate position only — '}
        {poi.approximateLatitude !== null && poi.approximateLongitude !== null
          ? `${poi.approximateLatitude.toFixed(3)}, ${poi.approximateLongitude.toFixed(3)}`
          : 'not placed'}
      </span>
      <span className="loc-list-end">
        <ConversationEntry projectId={projectId} anchorType="PLACE" anchorId={String(poi.sourceId)} label={poi.displayLabel} />
      </span>
    </li>
  );
}
